import { Route, Routes } from 'react-router';
import { paymentCommitments } from '@tahi/core';
import { registerCommitmentsProvider } from '../../data/commitments';
import { onPaymentsUsable, runInstallmentCatchUp } from '../../data/commands/payments';
import { registerMethodProvider } from '../../data/methodOptions';
import { onModuleUsable } from '../../data/moduleHooks';
import { listPayees, loadPaymentsSnapshot, PAYMENTS_TABLES } from '../../data/repos/payments';
import { registerTile } from '../../tiles/registry';
import { registerWebModule } from '../registry';
import { copy } from './copy';
import { PaymentsIcon } from './PaymentsIcon';
import { InstallmentRoute } from './screens/InstallmentRoute';
import { PayeeDetailScreen } from './screens/PayeeDetailScreen';
import { PayeeFormRoute } from './screens/PayeeFormRoute';
import { PaymentsDashboard } from './screens/PaymentsDashboard';
import { PaymentsSettingsScreen } from './screens/PaymentsSettingsScreen';
import { PayRoute } from './screens/PayRoute';
import { CardsTile, DueThisWeekTile, NextPaymentTile } from './tiles/PaymentsTiles';

/** Mounted by ModuleRoute at /m/payments/*, so these paths are relative. */
function PaymentsScreen() {
  return (
    <Routes>
      <Route index element={<PaymentsDashboard />} />
      <Route path="new" element={<PayeeFormRoute />} />
      <Route path="settings" element={<PaymentsSettingsScreen />} />
      <Route path="p/:id" element={<PayeeDetailScreen />} />
      <Route path="p/:id/edit" element={<PayeeFormRoute />} />
      <Route path="p/:id/due/:period" element={<PayRoute />} />
      <Route path="p/:id/pay" element={<PayRoute />} />
      <Route path="p/:id/installments/new" element={<InstallmentRoute />} />
      <Route path="p/:id/installments/:iid" element={<InstallmentRoute />} />
    </Routes>
  );
}

registerWebModule({
  id: 'payments',
  navLabel: copy.navLabel,
  Icon: PaymentsIcon,
  Screen: PaymentsScreen,
});

registerTile({
  type: 'payments.next',
  moduleId: 'payments',
  title: copy.nextPayment,
  sizes: ['wide', 'small'],
  defaultSize: 'wide',
  defaultOnToday: true,
  Component: NextPaymentTile,
});

registerTile({
  type: 'payments.due-this-week',
  moduleId: 'payments',
  title: copy.dueThisWeek,
  sizes: ['small', 'wide'],
  defaultSize: 'small',
  Component: DueThisWeekTile,
});

registerTile({
  type: 'payments.cards',
  moduleId: 'payments',
  title: copy.cardBalances,
  sizes: ['wide', 'small', 'tall'],
  defaultSize: 'wide',
  Component: CardsTile,
});

// Budget's cash flow subtracts what's due per cutoff and month: fixed bills, utilities, loans,
// card installments and a card's balance from before Tahi; overdue dues in the period holding
// today (Budget's today, in the same time zone as commands). Other card statements never: their
// purchases counted when bought (decision 06).
registerCommitmentsProvider({
  id: 'payments',
  moduleId: 'payments',
  tables: PAYMENTS_TABLES,
  load: async (db, userId, range, today) =>
    paymentCommitments(await loadPaymentsSnapshot(db, userId), range, today),
});

// Spending's quick add (and v0.7.6 "From" on Spending and the Shopping buy sheet) offers each card
// as a payment method (card:<payeeId>), with its billing days for the statement hint.
registerMethodProvider({
  id: 'payments.cards',
  moduleId: 'payments',
  tables: ['payees'],
  load: async (db, userId) =>
    (await listPayees(db, userId))
      .filter((p) => p.kind === 'card')
      .map((p) => ({
        value: `card:${p.id}`,
        label: p.last4 ? `${p.name} ••${p.last4}` : p.name,
        card: {
          statementDay: p.statementDay,
          dueDays: p.dueDays,
          dueDaysAfterStatement: p.dueDaysAfterStatement ?? null,
        },
      })),
});

// Loan payment category and installment charges when Payments becomes usable; installment
// months that came while Spending was off are written once Spending is back.
onModuleUsable('payments', onPaymentsUsable);
onModuleUsable('spending', runInstallmentCatchUp);
